import { useState, useEffect } from 'react';
import { 
  GraduationCap, 
  MapPin, 
  Target,
  Briefcase,
  Loader2,
  CheckCircle2,
  X,
  Award,
  FileText,
  Plane,
  Heart,
  Info,
  Upload,
  Trash2
} from 'lucide-react';
import { usePortfolio } from '@contexts/PortfolioContext';
import portfolioService from '@services/portfolio.service';
import { getCgpaZone } from '@utils/cgpaZones';
import { usePlanRestrictions } from '@hooks/usePlanRestrictions';
import { filterExamCategoriesByPlan } from '@utils/planRestrictions';

const FresherPortfolio = () => {
  const {
    careerStage,
    pursuingCourse,
    courseDuration,
    campusName,
    fresherAddress,
    fresherState,
    fresherDistrict,
    cgpaRange,
    selectedAptitudeTopics,
    jobSector,
    selectedIndustryOutlook,
    toggleIndustryOutlook,
    updateSelectedIndustryOutlook,
    states,
    examType,
    customExam,
    selectedExams,
    opportunityType,
    abroadEducation,
    medicalIssue,
    resumeReady,
    resumeFile,
    updateAbroadEducation,
    updateMedicalIssue,
    updatePursuingCourse,
    updateCourseDuration,
    updateCampusName,
    updateFresherAddress,
    updateFresherState,
    updateFresherDistrict,
    updateCgpaRange,
    toggleAptitudeTopic,
    updateSelectedAptitudeTopics,
    updateJobSector,
    updateExamType,
    updateCustomExam,
    toggleExam,
    updateSelectedExams,
    updateOpportunityType,
    updateResumeReady,
    updateResumeFile,
  } = usePortfolio();

  const { currentPlan } = usePlanRestrictions();

  const [aptitudeCategories, setAptitudeCategories] = useState([]);
  const [selectedAptitudeCategory, setSelectedAptitudeCategory] = useState(null);
  const [selectedSubdomain, setSelectedSubdomain] = useState(null);
  const [aptitudeLoading, setAptitudeLoading] = useState(false);
  const [aptitudeError, setAptitudeError] = useState(null);

  const [examCategories, setExamCategories] = useState([]);
  const [selectedExamCategory, setSelectedExamCategory] = useState(null);
  const [examsLoading, setExamsLoading] = useState(false);
  const [examsError, setExamsError] = useState(null);

  const [abroadExamCategories, setAbroadExamCategories] = useState([]);
  const [selectedAbroadExamCategory, setSelectedAbroadExamCategory] = useState(null);
  const [abroadExamsLoading, setAbroadExamsLoading] = useState(false);
  const [abroadExamsError, setAbroadExamsError] = useState(null);

  const [jobIndustries, setJobIndustries] = useState({ privateIndustries: [], governmentCategories: [] });
  const [industriesLoading, setIndustriesLoading] = useState(false);
  const [industriesError, setIndustriesError] = useState(null);

  const [districts, setDistricts] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [districtsError, setDistrictsError] = useState(null);

  const [uploadingResume, setUploadingResume] = useState(false);

  // Course autocomplete state
  const [coursesList, setCoursesList] = useState([]);
  const [showCourseSuggestions, setShowCourseSuggestions] = useState(false);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [uploadError, setUploadError] = useState(null);

  const selectedTopicIds = selectedAptitudeTopics.map((t) => t.id);
  const selectedExamIds = selectedExams.map((e) => e.id);

  // Fetch courses list for autocomplete
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await portfolioService.getDegrees();
        const courses = response?.data?.data || response?.data || [];
        setCoursesList(courses);
      } catch (error) {
        console.error('Error fetching courses:', error);
        setCoursesList([]);
      }
    };

    fetchCourses();
  }, []);

  // Filter courses based on input
  useEffect(() => {
    if (pursuingCourse && pursuingCourse.length > 0) {
      const filtered = coursesList.filter(course =>
        course.name.toLowerCase().includes(pursuingCourse.toLowerCase())
      );
      setFilteredCourses(filtered);
    } else {
      setFilteredCourses([]);
    }
  }, [pursuingCourse, coursesList]);

  const courseDurationOptions = [
    { value: '2', label: '2 Years' },
    { value: '3', label: '3 Years' },
    { value: '4', label: '4 Years' },
    { value: '5', label: '5 Years' }
  ];

  const cgpaRangeOptions = [
    { value: '<6', label: 'CGPA < 6' },
    { value: '6-7', label: 'CGPA Between 6-7' },
    { value: '7-8', label: 'CGPA Between 7-8' },
    { value: '8-9', label: 'CGPA Between 8-9' },
    { value: '9+', label: 'CGPA 9+' }
  ];

  useEffect(() => {
    const fetchAptitudeTopics = async () => {
      try {
        setAptitudeLoading(true);
        setAptitudeError(null);
        const response = await portfolioService.getAptitudeTopics({ grouped: true });
        const list = response?.data?.data || response?.data || [];
        const data = Array.isArray(list) ? list : [];
        setAptitudeCategories(data);
        if (data.length > 0) {
          setSelectedAptitudeCategory(data[0].category);
          setSelectedSubdomain(data[0].subdomains?.[0]?.subdomain || null);
        }
      } catch (error) {
        console.error('Error fetching aptitude topics:', error);
        setAptitudeError('Failed to load aptitude topics. Please try again.');
        setAptitudeCategories([]);
      } finally {
        setAptitudeLoading(false);
      }
    };

    fetchAptitudeTopics();
  }, []);

  useEffect(() => {
    const fetchDomesticExams = async () => {
      if (examType !== 'admission') {
        setExamCategories([]);
        setSelectedExamCategory(null);
        return;
      }

      try {
        setExamsLoading(true);
        setExamsError(null);
        const params = {
          userStage: 'fresher',
          examPurposeType: 'admission',
          groupByCategory: true,
          isAbroad: false,
        };
        const response = await portfolioService.getExams(params);

        const groups = response?.data?.data || response?.data || [];
        const allCategories = Array.isArray(groups) ? groups : [];
        const filteredCategories = filterExamCategoriesByPlan(allCategories, currentPlan?.name);
        setExamCategories(filteredCategories);
        if (filteredCategories.length > 0) {
          setSelectedExamCategory(filteredCategories[0].category);
        }
      } catch (error) {
        console.error('Error fetching domestic exams:', error);
        setExamsError('Failed to load exams. Please try again.');
        setExamCategories([]);
      } finally {
        setExamsLoading(false);
      }
    };

    fetchDomesticExams();
  }, [examType]);

  useEffect(() => {
    const fetchAbroadExams = async () => {
      if (!abroadEducation) {
        setAbroadExamCategories([]);
        setSelectedAbroadExamCategory(null);
        return;
      }

      try {
        setAbroadExamsLoading(true);
        setAbroadExamsError(null);
        const params = {
          userStage: 'fresher',
          examPurposeType: 'admission',
          groupByCategory: true,
          isAbroad: true,
        };
        const response = await portfolioService.getExams(params);

        const groups = response?.data?.data || response?.data || [];
        const allCategories = Array.isArray(groups) ? groups : [];
        const filteredCategories = filterExamCategoriesByPlan(allCategories, currentPlan?.name);
        setAbroadExamCategories(filteredCategories);
        if (filteredCategories.length > 0) {
          setSelectedAbroadExamCategory(filteredCategories[0].category);
        }
      } catch (error) {
        console.error('Error fetching abroad exams:', error);
        setAbroadExamsError('Failed to load abroad exams. Please try again.');
        setAbroadExamCategories([]);
      } finally {
        setAbroadExamsLoading(false);
      }
    };

    fetchAbroadExams();
  }, [examType, abroadEducation]);

  useEffect(() => {
    const fetchJobIndustries = async () => {
      if (examType !== 'job') {
        return;
      }
      try {
        setIndustriesLoading(true);
        setIndustriesError(null);
        const response = await portfolioService.getJobIndustries();
        const data = response?.data?.data || { privateIndustries: [], governmentCategories: [] };
        setJobIndustries(data);
      } catch (error) {
        console.error('Error fetching job industries:', error);
        setIndustriesError('Failed to load industries. Please try again.');
        setJobIndustries({ privateIndustries: [], governmentCategories: [] });
      } finally {
        setIndustriesLoading(false);
      }
    };

    fetchJobIndustries();
  }, [examType]);

  useEffect(() => {
    const fetchDistricts = async () => {
      if (!fresherState) {
        setDistricts([]);
        return;
      }
      try {
        setDistrictsLoading(true);
        setDistrictsError(null);
        const response = await portfolioService.getDistrictsByState({ stateName: fresherState });
        const list = response?.data?.data || [];
        setDistricts(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Error fetching districts:', error);
        setDistrictsError('Failed to load districts. Please try again.');
        setDistricts([]);
      } finally {
        setDistrictsLoading(false);
      }
    };

    fetchDistricts();
  }, [fresherState]);

  const currentCategoryExams =
    examCategories.find((c) => c.category === selectedExamCategory)?.exams || [];

  const currentAbroadCategoryExams =
    abroadExamCategories.find((c) => c.category === selectedAbroadExamCategory)?.exams || [];

  const currentAptitudeCategory = aptitudeCategories.find(
    (c) => c.category === selectedAptitudeCategory
  );

  const currentSubdomain = currentAptitudeCategory?.subdomains.find(
    (sd) => sd.subdomain === selectedSubdomain
  );

  const handleAptitudeCategoryChange = (categoryName) => {
    setSelectedAptitudeCategory(categoryName);
    const cat = aptitudeCategories.find((c) => c.category === categoryName);
    setSelectedSubdomain(cat?.subdomains?.[0]?.subdomain || null);
  };

  const toggleTopicSelection = (topic) => {
    toggleAptitudeTopic(topic);
  };

  const toggleSubdomainSelection = (topics) => {
    const topicIds = topics.map((t) => t.id);
    const allSelected = topicIds.every((id) => selectedTopicIds.includes(id));
    if (allSelected) {
      updateSelectedAptitudeTopics(
        selectedAptitudeTopics.filter((t) => !topicIds.includes(t.id))
      );
    } else {
      const toAdd = topics
        .filter((t) => !selectedTopicIds.includes(t.id))
        .map((t) => ({
          id: t.id,
          topic: t.topic,
          category: currentAptitudeCategory?.category,
          subdomain: currentSubdomain?.subdomain,
        }));
      updateSelectedAptitudeTopics([...selectedAptitudeTopics, ...toAdd]);
    }
  };

  return (
    <div className="w-full min-w-0 max-w-full overflow-x-hidden space-y-3">
      {/* Hero Banner Section */}
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 rounded-lg shadow-lg overflow-hidden">
        <div className="relative px-6 py-8">
          {/* Decorative background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary-400 rounded-full blur-3xl"></div>
          </div>
          
          {/* Content */}
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Profile Avatar */}
            <div className="flex-shrink-0">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-white shadow-xl flex items-center justify-center overflow-hidden border-4 border-white/20">
                <div className="w-full h-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                  <span className="text-3xl md:text-4xl font-bold text-white">FP</span>
                </div>
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Fresher Career Portfolio
              </h1>
              <p className="text-sm text-slate-300 mb-3">
                Build your profile for job placements and career opportunities
              </p>
              
              {/* Info badges */}
              <div className="flex flex-wrap gap-2">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                  <Briefcase className="w-4 h-4 text-white" />
                  <span className="text-sm font-medium text-white">Fresher</span>
                </div>
                {pursuingCourse && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                    <GraduationCap className="w-4 h-4 text-white" />
                    <span className="text-sm font-medium text-white">{pursuingCourse}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 lg:p-5 space-y-3 md:space-y-4 max-w-full overflow-hidden">

        {/* Education Section */}
        <div>
          {/* Pursuing Course Section */}
          <div className="space-y-3">
            <h3 className="text-sm sm:text-base font-bold text-gray-900 flex items-center gap-2">
              <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-secondary-600" />
              Educational Details
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
              <div className="relative">
                <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-2">
                  Enter Course Name *
                </label>
                <input
                  type="text"
                  value={pursuingCourse}
                  onChange={(e) => {
                    updatePursuingCourse(e.target.value);
                    setShowCourseSuggestions(true);
                  }}
                  onFocus={() => setShowCourseSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowCourseSuggestions(false), 200)}
                  placeholder="e.g., B.Tech, BCA, MBA, M.Tech"
                  className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-1 focus:ring-secondary-500"
                />
                {showCourseSuggestions && filteredCourses.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredCourses.map((course) => (
                      <div
                        key={course.id}
                        onClick={() => {
                          updatePursuingCourse(course.name);
                          setShowCourseSuggestions(false);
                        }}
                        className="px-3 py-2 hover:bg-secondary-50 cursor-pointer text-sm text-gray-900 border-b border-gray-100 last:border-b-0"
                      >
                        {course.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-2">
                  Course Duration *
                </label>
                <select
                  value={courseDuration}
                  onChange={(e) => updateCourseDuration(e.target.value)}
                  className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-1 focus:ring-secondary-500"
                >
                  <option value="">Select duration</option>
                  {courseDurationOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-2">
                  Enter Campus Name *
                </label>
                <input
                  type="text"
                  value={campusName}
                  onChange={(e) => updateCampusName(e.target.value)}
                  placeholder="e.g., ABC College of Engineering"
                  className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-1 focus:ring-secondary-500"
                />
              </div>
            </div>
          </div>

        </div>

        {/* Location Section */}
        <div className="space-y-3 border-t border-gray-200 pt-4">
          <h3 className="text-sm sm:text-base font-bold text-gray-900 flex items-center gap-2">
            <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-secondary-600" />
            Campus Location Details
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full">
            <div className="md:col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-2">
                Address *
              </label>
              <input
                type="text"
                value={fresherAddress}
                onChange={(e) => updateFresherAddress(e.target.value)}
                placeholder="Enter your address"
                className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-1 focus:ring-secondary-500"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-2">
                State *
              </label>
              <select
                value={fresherState}
                onChange={(e) => {
                  updateFresherState(e.target.value);
                  updateFresherDistrict('');
                }}
                className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-1 focus:ring-secondary-500"
              >
                <option value="">Select a state</option>
                {states.map(state => (
                  <option key={state.id} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-2">
                District *
              </label>
              <select
                value={fresherDistrict}
                onChange={(e) => updateFresherDistrict(e.target.value)}
                disabled={!fresherState || districtsLoading}
                className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-1 focus:ring-secondary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">
                  {!fresherState
                    ? 'Select a state first'
                    : districtsLoading
                    ? 'Loading districts...'
                    : 'Select a district'}
                </option>
                {districts.map(district => (
                  <option key={district.id} value={district.name}>
                    {district.name}
                  </option>
                ))}
              </select>
              {districtsError && (
                <p className="text-xs text-red-600 mt-1">{districtsError}</p>
              )}
            </div>
          </div>
        </div>

        {/* Academic Performance / CGPA Section */}
        <div className="space-y-3 border-t border-gray-200 pt-4">
          <h3 className="text-sm sm:text-base font-bold text-gray-900 flex items-center gap-2">
            <Target className="w-4 h-4 sm:w-5 sm:h-5 text-secondary-600" />
            Academic Performance
          </h3>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-2">
              CGPA Range *
            </label>
            <select
              value={cgpaRange}
              onChange={(e) => updateCgpaRange(e.target.value)}
              className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-1 focus:ring-secondary-500"
            >
              <option value="">Select CGPA range</option>
              {cgpaRangeOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            {cgpaRange && (() => {
              const zone = getCgpaZone(cgpaRange);
              if (!zone) return null;
              return (
                <div className={`mt-3 rounded-xl border ${zone.borderClass} ${zone.bgClass} p-3 sm:p-4`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: zone.dotColor }}
                    />
                    <span className={`text-sm sm:text-base font-bold ${zone.textClass}`}>
                      {zone.name}
                    </span>
                    <span className="ml-auto text-[10px] sm:text-xs font-semibold text-gray-600 bg-white px-2 py-0.5 rounded-md border border-gray-200">
                      CGPA {cgpaRange}
                    </span>
                  </div>
                  <p className="text-[11px] sm:text-xs text-gray-700 leading-relaxed">
                    {zone.description}
                  </p>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Exams Section */}
        <div className="space-y-3 border-t border-gray-200 pt-4">
          <h3 className="text-sm sm:text-base font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-secondary-600" />
            Exams
          </h3>

          <div className="w-full">
            <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-2">
              Select Exam Type
            </label>
            <select
              value={examType || ''}
              onChange={(e) => updateExamType(e.target.value)}
              className="w-full px-3 py-2 sm:py-2.5 lg:px-4 lg:py-3 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-1 focus:ring-secondary-500"
            >
              <option value="">Select exam type</option>
              <option value="admission">National Exam for Admission</option>
              {(careerStage === 'fresher' || careerStage === 'professional') && (
                <option value="job">Exam for Job</option>
              )}
            </select>
          </div>

          {(examType === 'custom' || examType === 'other') && (
            <div className="w-full">
              <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-2">
                Specify Exam Name
              </label>
              <input
                type="text"
                value={customExam}
                onChange={(e) => updateCustomExam(e.target.value)}
                placeholder="Enter exam name"
                className="w-full px-3 py-2 sm:py-2.5 lg:px-4 lg:py-3 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-1 focus:ring-secondary-500"
              />
            </div>
          )}

          {examType === 'job' && (
            <div className="w-full space-y-3 bg-white border border-gray-200 rounded-xl p-3 sm:p-4 lg:p-5">
              <div>
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <h3 className="text-sm sm:text-base font-bold text-gray-900">
                      Select the sector where you want to join
                    </h3>
                    <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
                      Choose between Government and Private sector jobs
                    </p>
                  </div>
                  <div className="group relative flex-shrink-0">
                    <Info className="w-4 h-4 sm:w-5 sm:h-5 text-secondary-500 cursor-help" />
                    <div className="absolute right-0 top-6 w-64 sm:w-72 p-3 bg-secondary-50 border border-secondary-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                      <p className="text-xs text-secondary-700 font-semibold mb-1">
                        💡 Quick Tip
                      </p>
                      <p className="text-xs text-gray-700">
                        After selecting your sector and industries, related job opportunities will appear in the <span className="font-semibold text-secondary-600">Opportunity tab</span> for you to explore!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { value: 'Government', label: 'Jobs In Government Sector' },
                  { value: 'Private', label: ' Jobs In Private Sector' },
                ].map((opt) => {
                  const isActive = jobSector === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        updateJobSector(opt.value);
                        updateSelectedIndustryOutlook([]);
                      }}
                      className={`w-full text-center py-3 sm:py-4 rounded-lg border text-sm sm:text-base font-semibold transition-all ${
                        isActive
                          ? 'border-secondary-500 bg-secondary-50 text-secondary-700'
                          : 'border-gray-200 text-gray-800 hover:border-gray-300 bg-white'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {jobSector && (
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <div className="flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-[#fee2e2] flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-4 h-4 text-[#c33c3d]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm sm:text-base font-bold text-gray-900">
                        {jobSector === 'Government' ? 'Government Categories' : 'Industry Outlook'}
                      </h3>
                      <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
                        {jobSector === 'Government'
                          ? 'Pick the categories you are interested in'
                          : 'Explore industries and pick the ones you are interested in'}
                      </p>
                    </div>
                  </div>

                  {industriesLoading && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 text-[#c33c3d] animate-spin mr-2" />
                      <span className="text-xs sm:text-sm text-gray-600">
                        Loading {jobSector === 'Government' ? 'categories' : 'industries'}...
                      </span>
                    </div>
                  )}

                  {!industriesLoading && industriesError && (
                    <div className="text-xs sm:text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      {industriesError}
                    </div>
                  )}

                  {!industriesLoading && !industriesError && (() => {
                    const items =
                      jobSector === 'Government'
                        ? jobIndustries.governmentCategories
                        : jobIndustries.privateIndustries;
                    if (!items || items.length === 0) {
                      return (
                        <div className="text-xs sm:text-sm text-gray-500 bg-white border border-dashed border-gray-300 rounded-lg px-3 py-6 text-center">
                          No {jobSector === 'Government' ? 'categories' : 'industries'} available.
                        </div>
                      );
                    }
                    return (
                      <>
                        <div className="flex flex-wrap gap-1.5">
                          {items.map((item) => {
                            const isSelected = selectedIndustryOutlook.includes(item);
                            return (
                              <button
                                key={item}
                                type="button"
                                onClick={() => toggleIndustryOutlook(item)}
                                className={`px-3.5 py-1.5 rounded-md text-xs sm:text-[13px] font-semibold transition-all whitespace-nowrap ${
                                  isSelected
                                    ? 'bg-[#c33c3d] text-white shadow-sm'
                                    : 'bg-white text-gray-700 border border-gray-200 hover:border-[#c33c3d] hover:text-[#c33c3d]'
                                }`}
                              >
                                {item}
                              </button>
                            );
                          })}
                        </div>

                        {selectedIndustryOutlook.length > 0 && (
                          <div className="bg-[#fee2e2]/50 border border-[#fecaca] rounded-lg p-3 mt-3">
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <CheckCircle2 className="w-4 h-4 text-[#c33c3d] flex-shrink-0" />
                                <span className="text-xs sm:text-sm font-semibold text-gray-800">
                                  Selected ({selectedIndustryOutlook.length})
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => updateSelectedIndustryOutlook([])}
                                className="text-[11px] sm:text-xs font-semibold text-[#c33c3d] hover:text-[#b02f30]"
                              >
                                Clear all
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {selectedIndustryOutlook.map((item) => (
                                <span
                                  key={item}
                                  className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-[#fee2e2] border border-[#fecaca] rounded-full text-[11px] sm:text-xs text-[#c33c3d] font-medium"
                                >
                                  {item}
                                  <button
                                    type="button"
                                    onClick={() => toggleIndustryOutlook(item)}
                                    className="ml-0.5 w-4 h-4 rounded-full flex items-center justify-center bg-[#fee2e2] hover:bg-[#fecaca] text-[#c33c3d]"
                                    aria-label={`Remove ${item}`}
                                  >
                                    <X className="w-2.5 h-2.5" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {examType === 'admission' && (
            <div className="w-full space-y-4 bg-white border border-gray-200 rounded-xl p-3 sm:p-4 lg:p-5">
              <div>
                <h3 className="text-sm sm:text-base font-bold text-gray-900">
                  National Admission Exams
                </h3>
                <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
                  Showing domestic entrance exams
                </p>
              </div>

              {examsLoading && (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-5 h-5 text-[#c33c3d] animate-spin mr-2" />
                  <span className="text-xs sm:text-sm text-gray-600">Loading exams...</span>
                </div>
              )}

              {!examsLoading && examsError && (
                <div className="text-xs sm:text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {examsError}
                </div>
              )}

              {!examsLoading && !examsError && examCategories.length === 0 && (
                <div className="text-xs sm:text-sm text-gray-500 bg-white border border-dashed border-gray-300 rounded-lg px-3 py-6 text-center">
                  No exams available.
                </div>
              )}

              {!examsLoading && !examsError && examCategories.length > 0 && (
                <>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {examCategories.map((cat) => {
                      const isActive = cat.category === selectedExamCategory;
                      return (
                        <button
                          key={cat.category}
                          type="button"
                          onClick={() => setSelectedExamCategory(cat.category)}
                          className={`flex-shrink-0 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[11px] sm:text-xs font-medium whitespace-nowrap transition-all border ${
                            isActive
                              ? 'bg-[#c33c3d] text-white border-[#c33c3d] shadow-sm'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-[#c33c3d] hover:text-[#c33c3d]'
                          }`}
                        >
                          {cat.category}
                          <span
                            className={`ml-1 inline-flex items-center justify-center text-[9px] sm:text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                              isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {cat.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    {currentCategoryExams.map((exam) => {
                      const isSelected = selectedExamIds.includes(exam.id);
                      return (
                        <button
                          key={exam.id}
                          type="button"
                          onClick={() => toggleExam(exam)}
                          className={`relative text-left rounded-xl border p-3 sm:p-4 pr-9 sm:pr-10 transition-all duration-200 ${
                            isSelected
                              ? 'border-[#c33c3d] bg-[#fee2e2]/60 shadow-md ring-2 ring-[#fecaca]'
                              : 'border-gray-200 bg-white hover:border-[#c33c3d] hover:shadow-sm'
                          }`}
                        >
                          <div
                            className={`absolute top-3 right-3 sm:top-3.5 sm:right-3.5 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center transition-all ${
                              isSelected
                                ? 'bg-[#c33c3d] border border-[#c33c3d] shadow-sm'
                                : 'bg-white border border-gray-300'
                            }`}
                            aria-hidden="true"
                          >
                            {isSelected && (
                              <svg
                                className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.704 5.296a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 011.414-1.414L8.5 12.086l6.79-6.79a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>

                          {isSelected && (
                            <span className="inline-flex items-center gap-1 mb-2 px-2 py-0.5 rounded-full bg-[#c33c3d] text-white text-[10px] sm:text-[11px] font-semibold">
                              <CheckCircle2 className="w-3 h-3" />
                              Selected
                            </span>
                          )}

                          <h4 className="text-sm sm:text-base font-bold text-gray-900 leading-snug">
                            {exam.examName}
                            <span className="font-bold"> ({exam.fullForm})</span>
                          </h4>

                          <p className="text-xs sm:text-sm text-[#c33c3d] font-medium mt-1">
                            Category: {exam.category}
                          </p>

                          <div className="mt-3">
                            <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-1.5">
                              Purpose:
                            </p>
                            <p className="text-[11px] sm:text-xs text-gray-600 leading-relaxed">
                              {exam.purpose}
                            </p>
                          </div>

                          <div className="mt-3">
                            <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-1.5">
                              Eligibility:
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {exam.eligibility
                                .split(/[+,]/)
                                .map((s) => s.trim())
                                .filter(Boolean)
                                .map((tag, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#fee2e2] text-[#c33c3d] text-[10px] sm:text-[11px] font-medium"
                                  >
                                    {tag}
                                  </span>
                                ))}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {selectedExams.length > 0 && (
                    <div className="border-t border-gray-100 pt-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <CheckCircle2 className="w-4 h-4 text-[#c33c3d] flex-shrink-0" />
                          <span className="text-xs sm:text-sm font-semibold text-gray-800">
                            Selected Exams ({selectedExams.length})
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => updateSelectedExams([])}
                          className="text-[11px] sm:text-xs font-semibold text-[#c33c3d] hover:text-[#b02f30]"
                        >
                          Clear all
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedExams.map((exam) => (
                          <span
                            key={exam.id}
                            className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-[#fee2e2] border border-[#fecaca] rounded-full text-[11px] sm:text-xs text-[#c33c3d] font-medium"
                          >
                            {exam.examName}
                            <button
                              type="button"
                              onClick={() => toggleExam(exam)}
                              className="ml-0.5 w-4 h-4 rounded-full flex items-center justify-center bg-[#fee2e2] hover:bg-[#fecaca] text-[#c33c3d]"
                              aria-label={`Remove ${exam.examName}`}
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Preferences Section - Always visible */}
          <div className="w-full space-y-3 bg-white border border-gray-200 rounded-xl p-3 sm:p-4 lg:p-5">
            <div className="space-y-2">
              <h3 className="text-sm sm:text-base font-bold text-gray-900">
                Your Preferences
              </h3>
              <p className="text-[11px] sm:text-xs text-gray-500">
                These help us suggest the right exams for you
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 w-full pt-1">
                <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 lg:p-4 border border-gray-300 rounded-lg min-w-0">
                  <Plane className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 flex-shrink-0" />
                  <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                    <input
                      type="checkbox"
                      checked={abroadEducation}
                      onChange={(e) => updateAbroadEducation(e.target.checked)}
                      className="w-4 h-4 border-gray-300 rounded flex-shrink-0"
                    />
                    <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                      Abroad Education
                    </span>
                  </label>
                </div>

                <div className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 lg:p-4 border border-gray-300 rounded-lg min-w-0">
                  <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0" />
                  <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                    <input
                      type="checkbox"
                      checked={medicalIssue}
                      onChange={(e) => updateMedicalIssue(e.target.checked)}
                      className="w-4 h-4 border-gray-300 rounded flex-shrink-0"
                    />
                    <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                      Medical Issue
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Abroad Entrance Exams Section - Only shown when abroadEducation is checked */}
          {abroadEducation && (
            <div className="w-full space-y-4 bg-white border border-gray-200 rounded-xl p-3 sm:p-4 lg:p-5">
              <div>
                <h3 className="text-sm sm:text-base font-bold text-gray-900">
                  Abroad Entrance Exams
                </h3>
                <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
                  International entrance exams for studying abroad
                </p>
              </div>

              {abroadExamsLoading && (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-5 h-5 text-[#1a2642] animate-spin mr-2" />
                  <span className="text-xs sm:text-sm text-gray-600">Loading abroad exams...</span>
                </div>
              )}

              {!abroadExamsLoading && abroadExamsError && (
                <div className="text-xs sm:text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {abroadExamsError}
                </div>
              )}

              {!abroadExamsLoading && !abroadExamsError && abroadExamCategories.length === 0 && (
                <div className="text-xs sm:text-sm text-gray-500 bg-white border border-dashed border-gray-300 rounded-lg px-3 py-6 text-center">
                  No abroad exams available.
                </div>
              )}

              {!abroadExamsLoading && !abroadExamsError && abroadExamCategories.length > 0 && (
                <>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {abroadExamCategories.map((cat) => {
                      const isActive = cat.category === selectedAbroadExamCategory;
                      return (
                        <button
                          key={cat.category}
                          type="button"
                          onClick={() => setSelectedAbroadExamCategory(cat.category)}
                          className={`flex-shrink-0 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[11px] sm:text-xs font-medium whitespace-nowrap transition-all border ${
                            isActive
                              ? 'bg-[#1a2642] text-white border-[#1a2642] shadow-sm'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-[#1a2642] hover:text-[#1a2642]'
                          }`}
                        >
                          {cat.category}
                          <span
                            className={`ml-1 inline-flex items-center justify-center text-[9px] sm:text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                              isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {cat.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    {currentAbroadCategoryExams.map((exam) => {
                      const isSelected = selectedExamIds.includes(exam.id);
                      return (
                        <button
                          key={exam.id}
                          type="button"
                          onClick={() => toggleExam(exam)}
                          className={`relative text-left rounded-xl border p-3 sm:p-4 pr-9 sm:pr-10 transition-all duration-200 ${
                            isSelected
                              ? 'border-[#1a2642] bg-[#e8eaf0] shadow-md ring-2 ring-[#c5c9d6]'
                              : 'border-gray-200 bg-white hover:border-[#1a2642] hover:shadow-sm'
                          }`}
                        >
                          <div
                            className={`absolute top-3 right-3 sm:top-3.5 sm:right-3.5 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center transition-all ${
                              isSelected
                                ? 'bg-[#1a2642] border border-[#1a2642] shadow-sm'
                                : 'bg-white border border-gray-300'
                            }`}
                            aria-hidden="true"
                          >
                            {isSelected && (
                              <svg
                                className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.704 5.296a1 1 0 010 1.414l-7.5 7.5a1 1 0 01-1.414 0l-3.5-3.5a1 1 0 011.414-1.414L8.5 12.086l6.79-6.79a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>

                          {isSelected && (
                            <span className="inline-flex items-center gap-1 mb-2 px-2 py-0.5 rounded-full bg-[#1a2642] text-white text-[10px] sm:text-[11px] font-semibold">
                              <CheckCircle2 className="w-3 h-3" />
                              Selected
                            </span>
                          )}

                          <h4 className="text-sm sm:text-base font-bold text-gray-900 leading-snug">
                            {exam.examName}
                            {exam.fullForm && <span className="font-bold"> ({exam.fullForm})</span>}
                          </h4>

                          <p className="text-xs sm:text-sm text-[#1a2642] font-medium mt-1">
                            Category: {exam.category}
                          </p>

                          {exam.purpose && (
                            <div className="mt-3">
                              <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-1.5">
                                Purpose:
                              </p>
                              <p className="text-[11px] sm:text-xs text-gray-600 leading-relaxed">
                                {exam.purpose}
                              </p>
                            </div>
                          )}

                          {exam.eligibility && (
                            <div className="mt-3">
                              <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-1.5">
                                Eligibility:
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {exam.eligibility
                                  .split(/[+,]/)
                                  .map((s) => s.trim())
                                  .filter(Boolean)
                                  .map((tag, idx) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center px-2 py-0.5 rounded-md bg-[#e8eaf0] text-[#1a2642] text-[10px] sm:text-[11px] font-medium"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                              </div>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Aptitude Preparation Topics Section */}
        <div className="w-full bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-start gap-2.5 px-3 sm:px-4 py-2.5 border-b border-gray-100 bg-gray-50/50">
            <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
              <Target className="w-4 h-4 text-secondary-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm sm:text-base font-bold text-gray-900">
                Aptitude Preparation Topics
              </h3>
              <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
                Pick a category, choose a subdomain, then select the topics you want to prepare.
              </p>
            </div>
          </div>

          <div className="p-3 sm:p-4 space-y-3">
            {aptitudeLoading && (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-5 h-5 text-secondary-500 animate-spin mr-2" />
                <span className="text-xs sm:text-sm text-gray-600">Loading topics...</span>
              </div>
            )}

            {!aptitudeLoading && aptitudeError && (
              <div className="text-xs sm:text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {aptitudeError}
              </div>
            )}

            {!aptitudeLoading && !aptitudeError && aptitudeCategories.length === 0 && (
              <div className="text-xs sm:text-sm text-gray-500 bg-white border border-dashed border-gray-300 rounded-lg px-3 py-6 text-center">
                No aptitude topics available.
              </div>
            )}

            {!aptitudeLoading && !aptitudeError && aptitudeCategories.length > 0 && (
              <>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                    Category
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {aptitudeCategories.map((cat) => {
                      const isActive = cat.category === selectedAptitudeCategory;
                      return (
                        <button
                          key={cat.category}
                          type="button"
                          onClick={() => handleAptitudeCategoryChange(cat.category)}
                          className={`px-3.5 py-1.5 rounded-md text-xs sm:text-[13px] font-semibold transition-all whitespace-nowrap ${
                            isActive
                              ? 'bg-secondary-500 text-white shadow-sm'
                              : 'bg-white text-gray-700 border border-gray-200 hover:border-secondary-400 hover:text-secondary-600'
                          }`}
                        >
                          {cat.category}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {currentAptitudeCategory && currentAptitudeCategory.subdomains.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                      Subdomain
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {currentAptitudeCategory.subdomains.map((sd) => {
                        const isActive = sd.subdomain === selectedSubdomain;
                        const selectedCount = sd.topics.filter((t) =>
                          selectedTopicIds.includes(t.id)
                        ).length;
                        return (
                          <button
                            key={sd.subdomain}
                            type="button"
                            onClick={() => setSelectedSubdomain(sd.subdomain)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] sm:text-xs font-medium transition-all border ${
                              isActive
                                ? 'bg-secondary-500 text-white border-secondary-500'
                                : 'bg-white text-gray-700 border-gray-200 hover:border-secondary-300 hover:text-secondary-600'
                            }`}
                          >
                            {sd.subdomain}
                            {selectedCount > 0 && (
                              <span
                                className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold ${
                                  isActive
                                    ? 'bg-white text-secondary-600'
                                    : 'bg-secondary-100 text-secondary-700'
                                }`}
                              >
                                {selectedCount}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {currentSubdomain && (
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                        Topics
                      </p>
                      <button
                        type="button"
                        onClick={() => toggleSubdomainSelection(currentSubdomain.topics)}
                        className="text-[10px] sm:text-[11px] font-semibold text-secondary-600 hover:text-secondary-700 whitespace-nowrap"
                      >
                        {currentSubdomain.topics.every((t) =>
                          selectedTopicIds.includes(t.id)
                        )
                          ? 'Clear all'
                          : 'Select all'}
                      </button>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 sm:p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs sm:text-sm font-bold text-gray-900">
                          {currentSubdomain.subdomain}
                        </h4>
                        <span className="text-[10px] text-gray-500">
                          {
                            currentSubdomain.topics.filter((t) =>
                              selectedTopicIds.includes(t.id)
                            ).length
                          }{' '}
                          / {currentSubdomain.topics.length} selected
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 max-h-40 sm:max-h-48 overflow-y-auto pr-1">
                        {currentSubdomain.topics.map((t) => {
                          const checked = selectedTopicIds.includes(t.id);
                          return (
                            <button
                              key={t.id}
                              type="button"
                              onClick={() =>
                                toggleTopicSelection({
                                  id: t.id,
                                  topic: t.topic,
                                  category: currentAptitudeCategory?.category,
                                  subdomain: currentSubdomain?.subdomain,
                                })
                              }
                              className={`px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-medium transition-all whitespace-nowrap ${
                                checked
                                  ? 'bg-secondary-500 text-white border border-secondary-500 shadow-sm'
                                  : 'bg-white text-gray-700 border border-gray-300 hover:border-secondary-400 hover:text-secondary-600'
                              }`}
                            >
                              {t.topic}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {selectedAptitudeTopics.length > 0 && (
                  <div className="border-t border-gray-100 pt-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <CheckCircle2 className="w-4 h-4 text-secondary-600 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-semibold text-gray-800">
                          Your Selection ({selectedAptitudeTopics.length})
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => updateSelectedAptitudeTopics([])}
                        className="text-[11px] sm:text-xs font-semibold text-secondary-600 hover:text-secondary-700"
                      >
                        Clear all
                      </button>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {selectedAptitudeTopics.map((t) => (
                        <span
                          key={t.id}
                          className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-secondary-50 border border-secondary-200 rounded-full text-[11px] sm:text-xs text-secondary-700 font-medium"
                        >
                          <span className="text-gray-500 font-normal text-[10px] sm:text-[11px]">
                            {t.subdomain}
                          </span>
                          <span className="text-gray-400">/</span>
                          {t.topic}
                          <button
                            type="button"
                            onClick={() => toggleAptitudeTopic(t)}
                            className="ml-0.5 w-4 h-4 rounded-full flex items-center justify-center bg-secondary-100 hover:bg-secondary-200 text-secondary-600"
                            aria-label={`Remove ${t.topic}`}
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Resume Ready Section */}
        <div className="space-y-3 border-t border-gray-200 pt-4">
          <h3 className="text-sm sm:text-base font-bold text-gray-900 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-secondary-600" />
            Resume Ready
          </h3>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-2">
              Is your resume ready? *
            </label>
            <select
              value={resumeReady}
              onChange={(e) => updateResumeReady(e.target.value)}
              className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-secondary-500"
            >
              <option value="">Select an option</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>

            {resumeReady && (
              <div className={`mt-3 rounded-xl border-2 p-3 sm:p-4 ${
                resumeReady === 'yes' 
                  ? 'border-green-200 bg-green-50' 
                  : 'border-orange-200 bg-orange-50'
              }`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span
                    className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: resumeReady === 'yes' ? '#10b981' : '#f97316' }}
                  />
                  <span className={`text-sm sm:text-base font-bold ${
                    resumeReady === 'yes' ? 'text-green-700' : 'text-orange-700'
                  }`}>
                    {resumeReady === 'yes' ? 'Resume Ready' : 'Resume Not Ready'}
                  </span>
                </div>
                <p className="text-[11px] sm:text-xs text-gray-700 leading-relaxed">
                  {resumeReady === 'yes' 
                    ? 'Great! Having a ready resume puts you ahead in the job application process.' 
                    : 'You might not realize it now, but not having a resume can quietly hold you back. Opportunities don\'t always come with a warning. A job opening, internship, or referral can appear suddenly—and when it does, the first thing you\'ll be asked for is your resume. A resume is simply a way to present who you are—your education, your skills, and what you\'re capable of. You don\'t need to be perfect. You just need to take the first step.'}
                </p>

                {resumeReady === 'yes' && (
                  <div className="mt-3 border-t border-green-200 pt-3">
                    <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-2">
                      Upload Your Resume (PDF, DOC, DOCX - Max 5MB)
                    </label>
                    
                    {!resumeFile ? (
                      <div className="border-2 border-dashed border-green-300 rounded-lg p-4 text-center hover:border-green-400 transition-colors">
                        <input
                          type="file"
                          id="resume-upload"
                          accept=".pdf,.doc,.docx"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;

                            const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
                            if (!allowedTypes.includes(file.type)) {
                              setUploadError('Please upload a valid resume file (PDF, DOC, or DOCX)');
                              return;
                            }

                            if (file.size > 5 * 1024 * 1024) {
                              setUploadError('File size must be less than 5MB');
                              return;
                            }

                            setUploadingResume(true);
                            setUploadError(null);

                            try {
                              const formData = new FormData();
                              formData.append('document', file);
                              formData.append('documentType', 'resume');

                              const response = await portfolioService.uploadDocument(formData);
                              
                              if (response.success) {
                                updateResumeFile({
                                  name: file.name,
                                  size: file.size,
                                  type: file.type,
                                  url: response.data?.data?.filePath || response.data?.filePath,
                                  uploadedAt: new Date().toISOString()
                                });
                              } else {
                                setUploadError('Failed to upload resume. Please try again.');
                              }
                            } catch (error) {
                              console.error('Resume upload error:', error);
                              setUploadError('Failed to upload resume. Please try again.');
                            } finally {
                              setUploadingResume(false);
                            }
                          }}
                          className="hidden"
                        />
                        <label htmlFor="resume-upload" className="cursor-pointer">
                          {uploadingResume ? (
                            <div className="flex items-center justify-center gap-2">
                              <Loader2 className="w-5 h-5 text-green-600 animate-spin" />
                              <span className="text-sm text-gray-600">Uploading...</span>
                            </div>
                          ) : (
                            <>
                              <Upload className="w-8 h-8 mx-auto text-green-600 mb-2" />
                              <span className="text-sm font-medium text-gray-700">Click to upload resume</span>
                              <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX (Max 5MB)</p>
                            </>
                          )}
                        </label>
                      </div>
                    ) : (
                      <div className="bg-white border-2 border-green-300 rounded-lg p-3 sm:p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            <FileText className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">{resumeFile.name}</p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {(resumeFile.size / 1024).toFixed(2)} KB • Uploaded {new Date(resumeFile.uploadedAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm('Are you sure you want to remove this resume?')) {
                                updateResumeFile(null);
                              }
                            }}
                            className="flex-shrink-0 p-1.5 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                            title="Remove resume"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}

                    {uploadError && (
                      <p className="text-xs text-red-600 mt-2">{uploadError}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FresherPortfolio;
